import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import bgImg from "../../assets/images/bg-image-2.png";
import { useAppContext } from "../../context/AppContext";
import { getPoses, getPoseProgress, recordPoseAnswer } from "../../services/poseService";
import { getRoots } from "../../services/rootsService";
import { createAdminPose, deleteAdminPose, deleteCloudinaryImage, updateAdminPose } from "../../services/adminService";
import CloudinaryImageUpload from "../utilPages/CloudinaryImageUpload";
import PoseFigure from "../yogaAsanas/PoseFigure";
import { POSE_CATEGORIES, poseMatchesSearch, weightedShufflePoses, isPoseLearned } from "../yogaAsanas/poseUtils";
import { poseRootLabel } from "../yogaAsanas/quizUtils";
import QuizPractice from "../yogaAsanas/QuizPractice";
import ExamMode from "../yogaAsanas/ExamMode";
import { cloudinaryPublicId } from "../../utils/cloudinaryUtils";
import styles from "./SanskritYogaPage.module.css";

const POSE_IMAGE_UPLOAD_FOLDER = "onceUpOnAMat/Asanas";
const EMPTY_POSE_FORM = { sanskrit: "", russian: "", english: "", category: "", shape: "", roots: [], imageUrl: "", isMain: false };

function FlashCard({ pose, flipped, onFlip, language, rootItems }) {
  return (
    <div
      className={`${styles.cardWrap} ${flipped ? styles.cardWrapFlipped : ""}`}
      onClick={onFlip}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onFlip();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className={styles.cardInner}>
        <div className={`${styles.cardFace} ${styles.cardFront}`}>
          <div className={styles.cardImgWrap}>
            <PoseFigure
              pose={pose}
              variant="card"
              imgClassName={styles.cardImg}
              wrapClassName={styles.cardImg}
            />
          </div>
          <p className={styles.cardHint}>нажми чтобы перевернуть</p>
        </div>
        <div className={`${styles.cardFace} ${styles.cardBack}`}>
          <div className={styles.cardLabel}>Перевод</div>
          <p className={styles.cbSkBack}>{pose.sanskrit}</p>
          <p className={styles.cbRu}>{pose.russian}</p>
          <p className={styles.cbEn}>{pose.english}</p>
          <div className={styles.cbRoots}>
            <div className={styles.cbRootsTitle}>Корни</div>
            {pose.roots.map((r, ri) => (
              <div key={`${r.root}-${ri}`} className={styles.cbRoot}>
                <span className={styles.cbRootSk}>{poseRootLabel(r.root, rootItems, language)}</span>
                <span className={styles.cbRootMn}>— {r.meaning}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Sanskrit/russian/english/category/shape/roots fields shared by the create and edit pose forms. */
function PoseFieldsEditor({ form, onField, onRootChange, onAddRoot, onRemoveRoot }) {
  return (
    <>
      <label className={styles.poseEditLabel}>
        Санскрит
        <input
          className={styles.poseEditInput}
          value={form.sanskrit}
          onChange={(e) => onField("sanskrit", e.target.value)}
          required
        />
      </label>
      <label className={styles.poseEditLabel}>
        Русский
        <input
          className={styles.poseEditInput}
          value={form.russian}
          onChange={(e) => onField("russian", e.target.value)}
          required
        />
      </label>
      <label className={styles.poseEditLabel}>
        Английский
        <input
          className={styles.poseEditInput}
          value={form.english}
          onChange={(e) => onField("english", e.target.value)}
          required
        />
      </label>
      <label className={styles.poseEditLabel}>
        Категория
        <select
          className={styles.poseEditInput}
          value={form.category}
          onChange={(e) => onField("category", e.target.value)}
          required
        >
          <option value="" disabled>Выберите категорию</option>
          {POSE_CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </label>
      <label className={styles.poseEditCheckboxLabel}>
        <input
          type="checkbox"
          checked={!!form.isMain}
          onChange={(e) => onField("isMain", e.target.checked)}
        />
        Основная асана
      </label>
      <div className={styles.poseEditRoots}>
        <div className={styles.poseEditRootsTitle}>Корни</div>
        {form.roots.map((r, idx) => (
          <div key={idx} className={styles.poseEditRootRow}>
            <input
              className={styles.poseEditInput}
              placeholder="корень"
              value={r.root}
              onChange={(e) => onRootChange(idx, "root", e.target.value)}
            />
            <input
              className={styles.poseEditInput}
              placeholder="значение"
              value={r.meaning}
              onChange={(e) => onRootChange(idx, "meaning", e.target.value)}
            />
            <button
              type="button"
              onClick={() => onRemoveRoot(idx)}
              className={styles.poseEditRootRemove}
              aria-label="Удалить корень"
            >
              ×
            </button>
          </div>
        ))}
        <button type="button" onClick={onAddRoot} className={styles.poseEditAddRoot}>
          + Добавить корень
        </button>
      </div>
    </>
  );
}

export default function SanskritYogaPage() {
  const { user, token, language } = useAppContext();
  const isAdmin = user?.role === "admin";
  const langCode = language === "english" ? "en" : "ru";

  const [allPoses, setAllPoses] = useState([]);
  const [rootGroups, setRootGroups] = useState([]);
  const [poseProgress, setPoseProgress] = useState({});

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    getPoses(token)
      .then((items) => {
        if (!cancelled) setAllPoses(items);
      })
      .catch(() => {});
    getRoots(token)
      .then((groups) => {
        if (!cancelled) setRootGroups(groups);
      })
      .catch(() => {});
    getPoseProgress(token)
      .then((items) => {
        if (cancelled) return;
        const bySanskrit = {};
        items.forEach((p) => {
          bySanskrit[p.sanskrit] = { correct: p.correct, incorrect: p.incorrect };
        });
        setPoseProgress(bySanskrit);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleQuizAnswer = useCallback(
    (pose, isCorrect) => {
      if (!pose) return;
      setPoseProgress((prev) => {
        const existing = prev[pose.sanskrit] || { correct: 0, incorrect: 0 };
        return {
          ...prev,
          [pose.sanskrit]: {
            correct: existing.correct + (isCorrect ? 1 : 0),
            incorrect: existing.incorrect + (isCorrect ? 0 : 1),
          },
        };
      });
      recordPoseAnswer(token, pose.sanskrit, isCorrect).catch(() => {});
    },
    [token]
  );

  const [poseScope, setPoseScope] = useState("main");
  const scopedPoses = useMemo(
    () => (poseScope === "main" ? allPoses.filter((p) => p.isMain) : allPoses),
    [allPoses, poseScope]
  );

  const learnedCount = useMemo(
    () => scopedPoses.filter((p) => isPoseLearned(poseProgress[p.sanskrit])).length,
    [scopedPoses, poseProgress]
  );
  const learnedPct = scopedPoses.length ? Math.round((learnedCount / scopedPoses.length) * 100) : 0;

  const rootItems = useMemo(() => rootGroups.flatMap((g) => g.items), [rootGroups]);

  const [tab, setTab] = useState("roots");
  const [poseSearch, setPoseSearch] = useState("");
  const [poseCategory, setPoseCategory] = useState(null);
  const [poseImageModal, setPoseImageModal] = useState(null);

  const handlePoseImageChange = useCallback((poseId, url) => {
    setAllPoses((prev) => prev.map((p) => (p._id === poseId ? { ...p, imageUrl: url || null } : p)));
    setPoseImageModal((prev) => (prev && prev._id === poseId ? { ...prev, imageUrl: url || null } : prev));
    updateAdminPose(token, poseId, { imageUrl: url }).catch(() => {});
  }, [token]);

  const handleToggleMain = useCallback((pose) => {
    const nextIsMain = !pose.isMain;
    setAllPoses((prev) => prev.map((p) => (p._id === pose._id ? { ...p, isMain: nextIsMain } : p)));
    updateAdminPose(token, pose._id, { isMain: nextIsMain }).catch(() => {
      setAllPoses((prev) => prev.map((p) => (p._id === pose._id ? { ...p, isMain: pose.isMain } : p)));
    });
  }, [token]);

  const handlePoseImageUploaded = useCallback(({ replacedUrl }) => {
    if (replacedUrl) {
      const publicId = cloudinaryPublicId(replacedUrl);
      if (publicId) deleteCloudinaryImage(token, publicId).catch(() => {});
    }
  }, [token]);

  const [cardCategory, setCardCategory] = useState(null);
  const filteredForCards = useMemo(() => {
    if (!cardCategory) return scopedPoses;
    return scopedPoses.filter((p) => p.category === cardCategory);
  }, [scopedPoses, cardCategory]);

  const [cardDeck, setCardDeck] = useState([]);
  const [cardIndex, setCardIndex] = useState(0);
  const [cardFlipped, setCardFlipped] = useState(false);

  useEffect(() => {
    setCardDeck(weightedShufflePoses(filteredForCards, poseProgress));
    setCardIndex(0);
    setCardFlipped(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredForCards]);

  const currentCard = cardDeck[cardIndex];
  const nCards = cardDeck.length;
  const progressPct = nCards ? ((cardIndex + 1) / nCards) * 100 : 0;

  const goPrev = useCallback(() => {
    if (!nCards) return;
    setCardFlipped(false);
    setCardIndex((i) => (i - 1 + nCards) % nCards);
  }, [nCards]);

  const goNext = useCallback(() => {
    if (!nCards) return;
    setCardFlipped(false);
    setCardIndex((i) => (i + 1) % nCards);
  }, [nCards]);

  const doShuffle = useCallback(() => {
    setCardDeck((d) => weightedShufflePoses(d, poseProgress));
    setCardIndex(0);
    setCardFlipped(false);
  }, [poseProgress]);

  const filteredPosesList = useMemo(() => {
    return scopedPoses
      .filter((p) => {
        if (poseCategory && p.category !== poseCategory) return false;
        return poseMatchesSearch(p, poseSearch);
      })
      .sort((a, b) => {
        const catDiff = POSE_CATEGORIES.indexOf(a.category) - POSE_CATEGORIES.indexOf(b.category);
        if (catDiff !== 0) return catDiff;
        return a.sanskrit.localeCompare(b.sanskrit);
      });
  }, [scopedPoses, poseCategory, poseSearch]);

  const [showAddPose, setShowAddPose] = useState(false);
  const [addPoseForm, setAddPoseForm] = useState(EMPTY_POSE_FORM);
  const [creatingPose, setCreatingPose] = useState(false);

  useEffect(() => {
    if (!poseImageModal && !showAddPose) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setPoseImageModal(null);
        setShowAddPose(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [poseImageModal, showAddPose]);

  useEffect(() => {
    if (!poseImageModal && !showAddPose) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [poseImageModal, showAddPose]);

  useEffect(() => {
    if (tab !== "poses") {
      setPoseImageModal(null);
      setShowAddPose(false);
    }
  }, [tab]);

  // Admin edit form — resets only when switching to a different pose, not on every field save.
  const [poseForm, setPoseForm] = useState(EMPTY_POSE_FORM);
  const [savingPose, setSavingPose] = useState(false);
  const [deletingPose, setDeletingPose] = useState(false);

  useEffect(() => {
    if (!poseImageModal) return;
    setPoseForm({
      sanskrit: poseImageModal.sanskrit || "",
      russian: poseImageModal.russian || "",
      english: poseImageModal.english || "",
      category: poseImageModal.category || "",
      shape: poseImageModal.shape || "",
      roots: (poseImageModal.roots || []).map((r) => ({ root: r.root, meaning: r.meaning })),
      isMain: !!poseImageModal.isMain,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poseImageModal?._id]);

  const updatePoseFormField = (field, value) => setPoseForm((f) => ({ ...f, [field]: value }));

  const updateRoot = (idx, field, value) => {
    setPoseForm((f) => {
      const roots = [...f.roots];
      roots[idx] = { ...roots[idx], [field]: value };
      return { ...f, roots };
    });
  };
  const addRoot = () => setPoseForm((f) => ({ ...f, roots: [...f.roots, { root: "", meaning: "" }] }));
  const removeRoot = (idx) => setPoseForm((f) => ({ ...f, roots: f.roots.filter((_, i) => i !== idx) }));

  const handleSavePose = async (e) => {
    e.preventDefault();
    if (!poseImageModal) return;
    setSavingPose(true);
    try {
      const payload = {
        sanskrit: poseForm.sanskrit.trim(),
        russian: poseForm.russian.trim(),
        english: poseForm.english.trim(),
        category: poseForm.category,
        shape: poseForm.shape,
        roots: poseForm.roots
          .map((r) => ({ root: r.root.trim(), meaning: r.meaning.trim() }))
          .filter((r) => r.root && r.meaning),
        isMain: poseForm.isMain,
      };
      const updated = await updateAdminPose(token, poseImageModal._id, payload);
      setAllPoses((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
      setPoseImageModal(null);
    } catch (err) {
      alert(err.message || "Не удалось сохранить изменения");
    } finally {
      setSavingPose(false);
    }
  };

  const handleDeletePose = async () => {
    if (!poseImageModal) return;
    if (!window.confirm(`Удалить позу «${poseImageModal.sanskrit}»?`)) return;
    setDeletingPose(true);
    try {
      await deleteAdminPose(token, poseImageModal._id);
      setAllPoses((prev) => prev.filter((p) => p._id !== poseImageModal._id));
      setPoseImageModal(null);
    } catch (err) {
      alert(err.message || "Не удалось удалить позу");
    } finally {
      setDeletingPose(false);
    }
  };

  const openAddPose = () => {
    setAddPoseForm(EMPTY_POSE_FORM);
    setShowAddPose(true);
  };

  const updateAddPoseField = (field, value) => setAddPoseForm((f) => ({ ...f, [field]: value }));

  const updateAddPoseRoot = (idx, field, value) => {
    setAddPoseForm((f) => {
      const roots = [...f.roots];
      roots[idx] = { ...roots[idx], [field]: value };
      return { ...f, roots };
    });
  };
  const addAddPoseRoot = () => setAddPoseForm((f) => ({ ...f, roots: [...f.roots, { root: "", meaning: "" }] }));
  const removeAddPoseRoot = (idx) => setAddPoseForm((f) => ({ ...f, roots: f.roots.filter((_, i) => i !== idx) }));

  const handleCreatePose = async (e) => {
    e.preventDefault();
    setCreatingPose(true);
    try {
      const payload = {
        sanskrit: addPoseForm.sanskrit.trim(),
        russian: addPoseForm.russian.trim(),
        english: addPoseForm.english.trim(),
        category: addPoseForm.category,
        shape: addPoseForm.shape,
        roots: addPoseForm.roots
          .map((r) => ({ root: r.root.trim(), meaning: r.meaning.trim() }))
          .filter((r) => r.root && r.meaning),
        imageUrl: addPoseForm.imageUrl,
        isMain: addPoseForm.isMain,
      };
      const created = await createAdminPose(token, payload);
      setAllPoses((prev) => [...prev, created]);
      setShowAddPose(false);
      setPoseImageModal(created);
    } catch (err) {
      alert(err.message || "Не удалось создать позу");
    } finally {
      setCreatingPose(false);
    }
  };

  return (
    <main className="flex min-h-screen flex-col border-t-[1.5px] border-black mt-5 text-left">
      <Helmet>
        <title>Санскрит — асаны | Однажды на коврике</title>
      </Helmet>

      <div className="relative h-[40vh] sm:h-[50vh] lg:h-[55vh] w-full overflow-hidden">
        <img
          src={bgImg}
          alt=""
          className="w-full h-full object-cover"
        />
        {scopedPoses.length > 0 && (
          <div className={styles.progressCard}>
            <p className={styles.progressCardTitle}>Прогресс</p>
            <div className={styles.progressCardPct}>{learnedPct}%</div>
            <div className={styles.progressCardBar}>
              <div className={styles.progressCardFill} style={{ width: `${learnedPct}%` }} />
            </div>
            <p className={styles.progressCardSub}>
              {learnedCount} из {scopedPoses.length} асан
            </p>
          </div>
        )}

        <div className={styles.scopeToggleOverlay}>
          <div className={styles.scopeToggle}>
            <button
              type="button"
              className={poseScope === "main" ? styles.scopeToggleOptionActive : styles.scopeToggleOption}
              onClick={() => setPoseScope("main")}
            >
              Основные
            </button>
            <button
              type="button"
              className={poseScope === "all" ? styles.scopeToggleOptionActive : styles.scopeToggleOption}
              onClick={() => setPoseScope("all")}
            >
              Все
            </button>
          </div>
        </div>
      </div>

      <div className="w-11/12 lg:w-10/12 mx-auto mb-10 pb-8">
        <div className={styles.page}>
          <div className={styles.tabs}>
            <button
              type="button"
              className={tab === "roots" ? styles.tabActive : styles.tab}
              onClick={() => setTab("roots")}
            >
              Корни
            </button>
            <button
              type="button"
              className={tab === "cards" ? styles.tabActive : styles.tab}
              onClick={() => setTab("cards")}
            >
              Карточки
            </button>
            <button
              type="button"
              className={tab === "poses" ? styles.tabActive : styles.tab}
              onClick={() => setTab("poses")}
            >
              Асаны
            </button>
            <button
              type="button"
              className={tab === "quiz" ? styles.tabActive : styles.tab}
              onClick={() => setTab("quiz")}
            >
              Квиз
            </button>
            <button
              type="button"
              className={tab === "exam" ? styles.tabActive : styles.tab}
              onClick={() => setTab("exam")}
            >
              Экзамен
            </button>
          </div>

          {tab === "roots" && (
        <section className={styles.section} aria-labelledby="roots-heading">
          <h1 id="roots-heading" className={styles.sectionTitle}>
            Ключи санскрита
          </h1>
          <p className={styles.sectionSub}>Корни — основа всех названий</p>
          {rootGroups.map((group) => (
            <div key={group.title[langCode]} className={styles.rootGroup}>
              <div className={styles.rgTitle}>{group.title[langCode]}</div>
              {group.items.map((row) => (
                <div key={row.sk} className={styles.rootItem}>
                  <div className={styles.rsk}>
                    {row.sk}
                    {row.transliteration ? <span className={styles.rtr}> ({row.transliteration})</span> : null}
                  </div>
                  <div>
                    <div className={styles.rmn}>{row.translations[langCode].meaning}</div>
                    <div className={styles.rex}>{row.translations[langCode].examples}</div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </section>
          )}

          {tab === "cards" && (
        <section className={styles.section} aria-labelledby="cards-heading">
          <h1 id="cards-heading" className={styles.sectionTitle}>
            Карточки
          </h1>
          <p className={styles.sectionSub}>
            Лицо — санскрит · Обратная — перевод и корни
          </p>

          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className={styles.cardCounter}>
            {nCards ? `${cardIndex + 1} / ${nCards}` : "0 / 0"}
          </div>

          {currentCard ? (
            <FlashCard
              pose={currentCard}
              flipped={cardFlipped}
              onFlip={() => setCardFlipped((f) => !f)}
              language={language}
              rootItems={rootItems}
            />
          ) : (
            <p className={styles.emptyNote}>Нет поз в выбранной категории.</p>
          )}

          <div className={styles.cardControls}>
            <button
              type="button"
              className={styles.btnShuffle}
              onClick={doShuffle}
            >
              Перемешать
            </button>
            <button type="button" className={styles.btnPrev} onClick={goPrev}>
              Назад
            </button>
            <button type="button" className={styles.btnNext} onClick={goNext}>
              Вперёд
            </button>
          </div>

          <div className={styles.filterRow}>
            <button
              type="button"
              className={
                cardCategory === null
                  ? styles.filterBtnActive
                  : styles.filterBtn
              }
              onClick={() => setCardCategory(null)}
            >
              Все
            </button>
            {POSE_CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                className={
                  cardCategory === c ? styles.filterBtnActive : styles.filterBtn
                }
                onClick={() => setCardCategory(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </section>
          )}

          {tab === "poses" && (
        <section className={styles.section} aria-labelledby="poses-heading">
          <h1 id="poses-heading" className={styles.sectionTitle}>
            Асаны
          </h1>
          <p className={styles.sectionSub}>С разбором корней</p>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Поиск по санскриту, русскому, английскому…"
            value={poseSearch}
            onChange={(e) => setPoseSearch(e.target.value)}
            aria-label="Поиск по названию"
          />
          <div className={styles.filterRow}>
            <button
              type="button"
              className={
                poseCategory === null
                  ? styles.filterBtnActive
                  : styles.filterBtn
              }
              onClick={() => setPoseCategory(null)}
            >
              Все
            </button>
            {POSE_CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                className={
                  poseCategory === c ? styles.filterBtnActive : styles.filterBtn
                }
                onClick={() => setPoseCategory(c)}
              >
                {c}
              </button>
            ))}
          </div>
          <div className={styles.posesCount}>
            Показано: {filteredPosesList.length} из {scopedPoses.length}
          </div>
          {isAdmin && (
            <button type="button" onClick={openAddPose} className={styles.addPoseBtn}>
              + Добавить позу
            </button>
          )}
          <div>
            {filteredPosesList.map((pose) => (
              <article key={pose._id} className={styles.poseItem}>
                <button
                  type="button"
                  className={`${styles.poseThumb} ${styles.poseThumbOpen}`}
                  onClick={() => setPoseImageModal(pose)}
                  aria-label={`Увеличить иллюстрацию: ${pose.sanskrit}`}
                >
                  <PoseFigure
                    pose={pose}
                    variant="list"
                    imgClassName={styles.thumbImg}
                    wrapClassName={styles.thumbImg}
                  />
                </button>
                <div className={styles.poseInfo}>
                  <div className={styles.poseHeader}>
                    <div className={styles.poseSk}>{pose.sanskrit}</div>
                    <div className={styles.poseHeaderRight}>
                      <span className={styles.poseTag}>{pose.category}</span>
                      {isAdmin && (
                        <button
                          type="button"
                          className={pose.isMain ? styles.poseMainToggleActive : styles.poseMainToggle}
                          onClick={() => handleToggleMain(pose)}
                          title={pose.isMain ? "Убрать из основных" : "Сделать основной"}
                          aria-label={pose.isMain ? "Убрать из основных" : "Сделать основной"}
                        >
                          ★
                        </button>
                      )}
                    </div>
                  </div>
                  <div className={styles.poseRu}>{pose.russian}</div>
                  <div className={styles.poseEn}>{pose.english}</div>
                  <div className={styles.poseRoots}>
                    {pose.roots.map((r, ri) => (
                      <span key={`${r.root}-${ri}`} className={styles.poseRootChip}>
                        {poseRootLabel(r.root, rootItems, language)} <span>· {r.meaning}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>

          {poseImageModal ? (
            <div
              className={styles.poseModalBackdrop}
              onClick={() => setPoseImageModal(null)}
              role="presentation"
            >
              <div
                className={styles.poseModalPanel}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="pose-modal-title"
              >
                <button
                  type="button"
                  className={styles.poseModalClose}
                  onClick={() => setPoseImageModal(null)}
                  aria-label="Закрыть"
                >
                  ×
                </button>
                <div className={styles.poseModalFigure}>
                  <PoseFigure
                    pose={poseImageModal}
                    variant="list"
                    imgClassName={styles.poseModalImg}
                    wrapClassName={styles.poseModalImg}
                  />
                </div>
                <h2 id="pose-modal-title" className={styles.poseModalTitle}>
                  {poseImageModal.sanskrit}
                </h2>
                <p className={styles.poseModalSub}>{poseImageModal.russian}</p>
                <p className={styles.poseModalEn}>{poseImageModal.english}</p>
                {isAdmin && (
                  <div className={styles.poseModalAdmin} onClick={(e) => e.stopPropagation()}>
                    <CloudinaryImageUpload
                      key={poseImageModal._id}
                      label="Изображение (только для админов)"
                      folder={POSE_IMAGE_UPLOAD_FOLDER}
                      value={poseImageModal.imageUrl || ""}
                      onChange={(url) => handlePoseImageChange(poseImageModal._id, url)}
                      onUploaded={handlePoseImageUploaded}
                      token={token}
                      center
                    />

                    <form className={styles.poseEditForm} onSubmit={handleSavePose}>
                      <PoseFieldsEditor
                        form={poseForm}
                        onField={updatePoseFormField}
                        onRootChange={updateRoot}
                        onAddRoot={addRoot}
                        onRemoveRoot={removeRoot}
                      />

                      <div className={styles.poseEditActions}>
                        <button type="submit" disabled={savingPose} className={styles.poseEditSave}>
                          {savingPose ? "Сохранение..." : "Сохранить изменения"}
                        </button>
                        <button
                          type="button"
                          onClick={handleDeletePose}
                          disabled={deletingPose}
                          className={styles.poseEditDelete}
                        >
                          {deletingPose ? "Удаление..." : "Удалить позу"}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {showAddPose ? (
            <div
              className={styles.poseModalBackdrop}
              onClick={() => setShowAddPose(false)}
              role="presentation"
            >
              <div
                className={`${styles.poseModalPanel} ${styles.poseModalPanelWide}`}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="add-pose-title"
              >
                <button
                  type="button"
                  className={styles.poseModalClose}
                  onClick={() => setShowAddPose(false)}
                  aria-label="Закрыть"
                >
                  ×
                </button>
                <h2 id="add-pose-title" className={styles.poseModalTitle}>
                  Новая поза
                </h2>
                <CloudinaryImageUpload
                  label="Изображение"
                  folder={POSE_IMAGE_UPLOAD_FOLDER}
                  value={addPoseForm.imageUrl}
                  onChange={(url) => updateAddPoseField("imageUrl", url)}
                  onUploaded={handlePoseImageUploaded}
                  token={token}
                  center
                />
                <form className={styles.poseEditForm} onSubmit={handleCreatePose}>
                  <PoseFieldsEditor
                    form={addPoseForm}
                    onField={updateAddPoseField}
                    onRootChange={updateAddPoseRoot}
                    onAddRoot={addAddPoseRoot}
                    onRemoveRoot={removeAddPoseRoot}
                  />
                  <div className={styles.poseEditActions}>
                    <button type="submit" disabled={creatingPose} className={styles.poseEditSave}>
                      {creatingPose ? "Создание..." : "Создать позу"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : null}
        </section>
          )}

          {tab === "quiz" && (
        <section className={styles.section} aria-labelledby="quiz-heading">
          <h1 id="quiz-heading" className={styles.sectionTitle}>
            Квиз
          </h1>
          <QuizPractice
            poses={scopedPoses}
            language={language}
            progress={poseProgress}
            onAnswer={handleQuizAnswer}
            rootItems={rootItems}
          />
        </section>
          )}

          {tab === "exam" && (
        <section className={styles.section} aria-labelledby="exam-heading">
          <h1 id="exam-heading" className={styles.sectionTitle}>
            Экзамен
          </h1>
          <ExamMode
            poses={scopedPoses}
            language={language}
            progress={poseProgress}
            onAnswer={handleQuizAnswer}
            rootItems={rootItems}
          />
        </section>
          )}
        </div>
      </div>
    </main>
  );
}
